/**
 * @file ServiceContainer.ts
 * @role Thin, typed registry of constructed services. There is exactly ONE composition
 *       root (Bootstrapper) that fills it. Deep views/components must NOT pull from it —
 *       they receive dependencies via constructor injection instead.
 * @owns The service map and typed register/resolve.
 * @reads Nothing.
 * @neverDoes Construct services itself. Act as a service locator at depth.
 * @invariants 13 (no singleton soup; one composition root).
 * @see docs/INVARIANTS.md, docs/AGENT_PROTOCOL.md
 */
import { GameError } from '@core/errors/GameError';

/** A typed handle for a service. The phantom `_type` carries T through register/resolve. */
export interface ServiceKey<T> {
  readonly id: symbol;
  readonly name: string;
  /** Phantom field — never read at runtime; preserves the type parameter. */
  readonly _type?: T;
}

export function createServiceKey<T>(name: string): ServiceKey<T> {
  return { id: Symbol(name), name };
}

export class ServiceContainer {
  private readonly map = new Map<symbol, unknown>();

  register<T>(key: ServiceKey<T>, value: T): void {
    if (this.map.has(key.id)) {
      throw new GameError('UNKNOWN_ERROR', `Service already registered: ${key.name}`);
    }
    this.map.set(key.id, value);
  }

  resolve<T>(key: ServiceKey<T>): T {
    const value = this.map.get(key.id);
    if (value === undefined) {
      throw new GameError('UNKNOWN_ERROR', `Service not registered: ${key.name}`);
    }
    return value as T;
  }

  has<T>(key: ServiceKey<T>): boolean {
    return this.map.has(key.id);
  }
}
